// AbMyReports — "Meine Meldungen": eigene, per Melden-Flag gemeldete Dokumente, offen + geloest
// (Vorbild OC-Pilot pages/MyReports.tsx). GET /reports/mine ist fuer jeden Modul-Nutzer offen
// (serverseitig auf den eigenen Actor gefiltert).

import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, FileScan } from 'lucide-react';
import { Card, Chip, btnGhost, fmtDateTime } from './ui';
import { listMyReports, getReportComments } from '../api';

const AbMyReports = ({ user, isDarkMode, t, onOpenDocument, onBack, showError }) => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all'); // all | open | done
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [threads, setThreads] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    listMyReports(user)
      .then((res) => { setRows(Array.isArray(res) ? res : []); setError(null); })
      .catch((e) => { setError(e.message); showError?.(e.message); })
      .finally(() => setLoading(false));
  }, [user, showError]);

  useEffect(() => { load(); }, [load]);

  const toggleThread = async (documentId) => {
    if (expanded === documentId) { setExpanded(null); return; }
    setExpanded(documentId);
    if (!threads[documentId]) {
      try {
        const c = await getReportComments(user, documentId);
        setThreads((m) => ({ ...m, [documentId]: Array.isArray(c) ? c : [] }));
      } catch (e) { showError?.(e.message); }
    }
  };

  const textMain = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const textMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';

  const openCount = rows.filter((r) => !r.resolved).length;
  const doneCount = rows.length - openCount;
  const shown = rows.filter((r) => filter === 'all' || (filter === 'open') === !r.resolved);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {typeof onBack === 'function' && (
          <button onClick={onBack} className={btnGhost(isDarkMode)}>
            <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
          </button>
        )}
        <div>
          <h2 className={`text-lg font-bold ${textMain}`}>{t('ab.shell.myReportsTitle')}</h2>
          <p className={`text-xs mt-0.5 ${textMuted}`}>{t('ab.shell.myReportsSubtitle')}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {[
          { id: 'all', label: `${t('ab.shell.myReportsTabAll')} (${rows.length})` },
          { id: 'open', label: `${t('ab.shell.myReportsTabOpen')} (${openCount})` },
          { id: 'done', label: `${t('ab.shell.myReportsTabDone')} (${doneCount})` },
        ].map(({ id, label }) => (
          <button key={id} type="button" onClick={() => setFilter(id)}
            className={`px-3.5 py-1.5 rounded-xl text-sm font-medium transition-all ${filter === id
              ? (isDarkMode ? 'bg-indigo-600/70 text-white border border-indigo-400/30' : 'bg-indigo-600 text-white')
              : (isDarkMode ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100')
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* 3-State */}
      {loading ? (
        <p className={`text-sm py-8 text-center ${textMuted}`}>{t('ab.loading')}</p>
      ) : error ? (
        <p className={`text-sm py-6 text-center ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.shell.myReportsLoadError')}: {error}</p>
      ) : rows.length === 0 ? (
        <Card isDarkMode={isDarkMode}>
          <p className={`text-sm ${textMuted}`}>{t('ab.shell.myReportsEmpty')}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {shown.map((r) => (
            <Card key={`${r.documentId}-${r.reportedAt}`} isDarkMode={isDarkMode} className="!p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <button type="button" onClick={() => onOpenDocument(r.documentId)} className="flex items-center gap-2 min-w-0 text-left">
                  <FileScan className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                  <span className={`text-sm font-semibold truncate ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>{r.fileName}</span>
                </button>
                <Chip tone={r.resolved ? 'green' : 'amber'} isDarkMode={isDarkMode}>
                  {r.resolved ? t('ab.shell.myReportsDone') : t('ab.shell.myReportsOpen')}
                </Chip>
              </div>
              <div className={`text-xs mt-1 ${textMuted}`}>{t('ab.shell.myReportsReportedOn', { date: fmtDateTime(r.reportedAt) })}</div>
              {r.lastComment && (
                <p className={`text-sm mt-2 ${textMain}`}>{r.lastComment}</p>
              )}
              <button type="button" onClick={() => toggleThread(r.documentId)} className={`text-xs mt-2 font-medium ${isDarkMode ? 'text-white/50 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                {expanded === r.documentId ? `▲ ${t('ab.shell.myReportsHistory')}` : `▾ ${t('ab.shell.myReportsHistory')}`}
              </button>
              {expanded === r.documentId && (
                !threads[r.documentId] ? (
                  <p className={`text-xs mt-2 ${textMuted}`}>{t('ab.loading')}</p>
                ) : threads[r.documentId].length === 0 ? (
                  <p className={`text-xs mt-2 ${textMuted}`}>{t('ab.shell.myReportsNoComments')}</p>
                ) : (
                  <ul className="space-y-1.5 mt-2">
                    {threads[r.documentId].map((c, i) => (
                      <li key={i} className={`text-xs ${textMain}`}>
                        <span className={textMuted}>{fmtDateTime(c.createdAt)} · {c.author} · {t(`ab.shell.commentKind.${c.kind}`)}:</span>{' '}{c.comment}
                      </li>
                    ))}
                  </ul>
                )
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AbMyReports;
